<template>
  <DetailsAsideLayout>
    <template v-slot:detailsContent>
      <DirectoryImport
        v-if="isItemBeingUpdated"
        :displayName="directoryName"
        :o365Id="item.id"
        :parentType="parentType"
      />
      <DirectorySync
        v-if="isItemBeingCreated || isItemBeingUpdated"
        :deactivateAction="deactivateAction"
        :displayName="item.name"
        :isItemBeingCreated="isItemBeingCreated"
        :isItemBeingUpdated="isItemBeingUpdated"
        :parentType="parentType"
        :reactivateAction="reactivateAction"
        @externalWindowClosed="refreshAndRedirect"
      >
        <DistributionGroupManagement
          v-if="showDistributionGroupManagement"
          :checkboxLabel="checkboxLabel"
          :parentType="parentType"
          :tooltipKbLink="tooltipKbLink"
          :tooltipText="tooltipText"
        />
      </DirectorySync>
      <div
        v-if="isItemBeingUpdated"
      >
        <UserAttributesExport
          :activated="true"
          :directoryName="directoryName"
          :exportAttributesKbUrl="getExportAttributesKbUrl"
        />
      </div>
    </template>
  </DetailsAsideLayout>
</template>
<script>
import * as Constants from 'client/util/Constants';
import { get, noop } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import AsidePanelChildComponentMethodsMixin from 'components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import DetailsAsideLayout from 'client/components/AsidePanel/Details/DetailsAsideLayout.vue';
import DirectoryImport from 'client/directories/components/DirectoryImport.vue';
import DirectorySync from 'client/directories/components/DirectorySync.vue';
import DistributionGroupManagement from 'client/directories/components/DistributionGroupManagement.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import O365Api from 'client/api/v2/O365Api';
import UserAttributesExport from 'client/directories/components/UserAttributesExport.vue';
import directoryRouteMixin from 'client/components/mixins/directoryRouteMixin';

const {
  DirectoryNameConsts,
  DirectoryTypeConsts,
  ItemTypeConsts,
  StoreModelMap,
  StoreModuleMap,
} = Constants;

const directoryCollection = StoreModuleMap[ItemTypeConsts.directory];
const office365Model = StoreModelMap[ItemTypeConsts.office365];

export default {
  name: 'O365Details',

  components: {
    DetailsAsideLayout,
    DirectoryImport,
    DirectorySync,
    DistributionGroupManagement,
    UserAttributesExport,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    directoryRouteMixin,
  ],

  props: {
    handleOauthRedirect: {
      type: Function,
      required: true,
    },
  },

  data: () => ({
    directoryName: DirectoryNameConsts[DirectoryTypeConsts.office365],
  }),

  notify,

  computed: {
    ...mapGetters(office365Model, [
      'getExportAttributesKbUrl',
      'item',
    ]),

    checkboxLabel() {
      return `Enable management of Security Groups and memberships in ${this.directoryName}`;
    },

    showDistributionGroupManagement() {
      return this.$hasFeatureFlag(this.$featureFlags.o365GroupExports);
    },

    tooltipKbLink() {
      return KbArticleUrls.distributionGroupManagement;
    },

    tooltipText() {
      return `After you enable group management, each group associated with this ${this.directoryName} directory will be managed by JumpCloud. Keep in mind that changes made to groups in ${this.directoryName} aren’t synced to JumpCloud.`;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  methods: {
    ...mapActions(directoryCollection, [
      'refreshCollection',
    ]),

    /**
     * @todo The DirectoryCollection doesn't delete items correctly because deleting an AD Domain
     * makes a different request than deleting a Microsoft 365 instance. The former calls DELETE
     * /api/v2/activedirectories/:id and the latter calls DELETE /api/v2/office365s. This will
     * be resolved by moving this logic to the DirectoryCollection at a later date.
     */
    deactivateAction() {
      const { $options } = this;

      O365Api
        .delete(this.item.id)
        .then(() => this.refreshAndRedirect())
        .catch((error) => {
          let message = `<strong>Unable to delete ${this.directoryName}!</strong>`;

          if (error && error.message) {
            message = `${message}<br>${error.message}`;
          }

          $options.notify(Constants.default.error, message);
        });
    },

    reactivateAction() {
      O365Api
        .reactivate(this.item.id)
        .then((reactivateUrl) => {
          const oauthUrl = get(reactivateUrl, 'data.location');
          this.handleOauthRedirect(oauthUrl);
        })
        .catch((error) => {
          let message = `<strong>Unable to reactivate ${this.directoryName}!</strong>`;

          if (error && error.message) {
            message = `${message}<br>${error.message}`;
          }

          notify(Constants.default.error, message);
        });
    },

    refreshAndRedirect() {
      this.$router.push(this.directoryTypeRoute(DirectoryTypeConsts.office365)).catch(noop);
      this.refreshCollection();
    },
  },
};
</script>
<style module>
@import '../../css/sections.css';
</style>
